import { useState, useEffect } from "react";
import { SEO } from "@/components/seo/SEO";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

const supportedIndustries = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "finance", label: "Finance" },
  { value: "logistics", label: "Logistics" },
  { value: "tech", label: "Technology" },
  { value: "realestate", label: "Real Estate" },
  { value: "health", label: "Health" },
  { value: "universal", label: "Universal / Custom Industry" },
];

const CompanyRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [businessSize, setBusinessSize] = useState("");
  const [industry, setIndustry] = useState("");
  const [customDataType, setCustomDataType] = useState("");
  const [customKPIs, setCustomKPIs] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { navigate("/auth", { replace: true }); return; }
        const { data: profile } = await supabase
          .from("profiles")
          .select("company_name")
          .eq("id", session.user.id)
          .single();
        if (profile?.company_name) { navigate("/", { replace: true }); return; }
      } catch (error) {
        console.error("Auth check failed:", error);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName || !employeeCount || !businessSize || !industry) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error("No authenticated session found.");

      const customConfig = industry === "universal"
        ? { custom_data_type: customDataType, custom_kpis: customKPIs.split(",").map(k => k.trim()).filter(Boolean) }
        : {};

      const { error } = await supabase
        .from("profiles")
        .upsert({
          id: session.user.id,
          company_name: companyName,
          employee_count: parseInt(employeeCount),
          business_size: businessSize,
          industry,
          custom_industry_config: customConfig,
        });

      if (error) throw error;

      trackEvent("registration_complete", { industry, business_size: businessSize });
      toast.success("Registration complete! Welcome to Veylo AI.");
      navigate("/", { replace: true });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5 py-12 px-4">
      <SEO title="Complete Your Registration — Veylo AI" description="Tell us about your company so Veylo AI can tailor analytics, KPIs and AI workflows to your industry." path="/company-registration" />
      <div className="max-w-lg w-full">
        <Card className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold">Complete Your Registration</h1>
            <p className="text-muted-foreground mt-2">Tell us about your company to personalize your experience</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="company-name" className="block text-sm font-medium mb-1.5">Company Name</label>
              <Input id="company-name" placeholder="Your Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
            </div>

            <div>
              <label htmlFor="employee-count" className="block text-sm font-medium mb-1.5">Number of Employees</label>
              <Input id="employee-count" type="number" placeholder="e.g., 25" value={employeeCount} onChange={(e) => setEmployeeCount(e.target.value)} required />
            </div>

            <div>
              <label htmlFor="business-size" className="block text-sm font-medium mb-1.5">Business Size</label>
              <Select value={businessSize} onValueChange={setBusinessSize} required>
                <SelectTrigger id="business-size"><SelectValue placeholder="Select Business Size" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small Business (1-10 employees)</SelectItem>
                  <SelectItem value="medium">Mid-Sized Business (11-100 employees)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (100+ employees)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="industry" className="block text-sm font-medium mb-1.5">Industry</label>
              <Select value={industry} onValueChange={setIndustry} required>
                <SelectTrigger id="industry"><SelectValue placeholder="Select Your Industry" /></SelectTrigger>
                <SelectContent>
                  {supportedIndustries.map((ind) => (
                    <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {industry === "universal" && (
                <p className="text-xs text-muted-foreground mt-1">
                  Define your own data types and KPIs for any industry not listed above.
                </p>
              )}
            </div>

            {industry === "universal" && (
              <>
                <div>
                  <label htmlFor="custom-data-type" className="block text-sm font-medium mb-1.5">Custom Data Type</label>
                  <Input id="custom-data-type" placeholder="e.g., Patient Records, Inventory Logs" value={customDataType} onChange={(e) => setCustomDataType(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="custom-kpis" className="block text-sm font-medium mb-1.5">Custom KPIs (comma-separated)</label>
                  <Textarea id="custom-kpis" placeholder="e.g., Patient Wait Time, Bed Occupancy Rate, Treatment Success Rate" value={customKPIs} onChange={(e) => setCustomKPIs(e.target.value)} rows={3} />
                </div>
              </>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? "Saving..." : "Get Started"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CompanyRegistration;
